import { internalPath } from "@/lib/paths";
import { salons } from "@/data/salons";

/**
 * Shared internal-linking block. Sends link equity from high-traffic pages
 * (homepage, guides) to the money pages: salon locations and core services.
 */

const CORE_SERVICE_LINKS: Array<{ href: string; label: string; desc: string }> = [
  { href: "/herrklippning-stockholm", label: "Herrklippning Stockholm", desc: "Priser, bokning och våra fyra salonger." },
  { href: "/taper-fade", label: "Taper fade & skin fade", desc: "Low, mid, high och skin fade av fade-specialister." },
  { href: "/skaggtrimning", label: "Skäggtrimning Stockholm", desc: "Formning, trimning och rakkniv." },
  { href: "/herrklippning-skaggtrimning", label: "Klippning + skägg", desc: "Komplett behandling i ett paket – 680 kr." },
  { href: "/drop-in-barber-stockholm", label: "Drop-in barberare", desc: "Kom förbi utan bokning i mån av plats." },
  { href: "/priser", label: "Prislista", desc: "Alla priser för klippning, skägg och rakning." },
];

interface Props {
  /** Small heading above the block. */
  heading?: string;
  intro?: string;
  className?: string;
}

export function InternalLinkHub({
  heading = "Populära sidor",
  intro = "Hitta rätt salong och tjänst – direktlänkar till våra mest besökta sidor.",
  className = "",
}: Props) {
  return (
    <section
      className={`container-page py-12 md:py-16 border-t border-border ${className}`}
      aria-labelledby="internal-link-hub-heading"
    >
      <div className="max-w-2xl mb-8">
        <div className="eyebrow">Snabblänkar</div>
        <h2 id="internal-link-hub-heading" className="mt-3 text-2xl md:text-3xl">
          {heading}
        </h2>
        <p className="mt-3 text-muted-foreground">{intro}</p>
      </div>

      <div className="grid gap-10 md:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-brass-deep mb-4">
            Våra salonger
          </h3>
          <ul className="space-y-3">
            {salons.map((s) => (
              <li key={s.slug}>
                <a
                  href={internalPath(s.url)}
                  className="group flex items-baseline justify-between gap-4 border-b border-border/60 pb-2 hover:border-brass transition-colors"
                >
                  <span className="text-base group-hover:text-brass-deep transition-colors">
                    Barberare {s.shortName}
                  </span>
                  <span className="shrink-0 text-sm text-muted-foreground">{s.city}</span>
                </a>
              </li>
            ))}
            <li>
              <a
                href={"/"}
                className="text-sm text-brass-deep hover:underline"
              >
                Se alla barberare i Stockholm
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-brass-deep mb-4">
            Populära tjänster
          </h3>
          <ul className="space-y-3">
            {CORE_SERVICE_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={internalPath(l.href)}
                  className="group block border-b border-border/60 pb-2 hover:border-brass transition-colors"
                >
                  <span className="text-base group-hover:text-brass-deep transition-colors">
                    {l.label}
                  </span>
                  <span className="block text-sm text-muted-foreground">{l.desc}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
